import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get, ref, set } from 'firebase/database';
import { useAuth, database, storage } from '@/contexts/AuthContext';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, X, Trash } from 'lucide-react';

const techStackOptions = [
  'React', 'Angular', 'Vue', 'Node.js', 'Python', 'Java', 'C++', 'JavaScript',
  'TypeScript', 'Django', 'Flask', 'Spring Boot', 'MongoDB', 'PostgreSQL',
  'MySQL', 'Firebase', 'AWS', 'Docker', 'Kubernetes', 'TensorFlow', 'PyTorch'
];

export default function EditProjectPage() {
  const { projectId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [githubLink, setGithubLink] = useState('');
  const [status, setStatus] = useState('');
  const [allowCollaboration, setAllowCollaboration] = useState(false);
  const [selectedTechStack, setSelectedTechStack] = useState([]);
  const [file, setFile] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState(null);
  const [existingFilePath, setExistingFilePath] = useState(null);

  // Fetch project
  useEffect(() => {
    if (!projectId) return;

    const fetchProject = async () => {
      try {
        const snapshot = await get(ref(database, `projects/${projectId}`));
        const project = snapshot.val();
        if (project) {
          setTitle(project.title);
          setDescription(project.description);
          setGithubLink(project.github_link);
          setStatus(project.status);
          setAllowCollaboration(project.allow_collaboration);
          setSelectedTechStack(project.tech_stack || []);
          setExistingFileUrl(project.file_url || null);
          setExistingFilePath(project.file_path || null); // store original file path in DB
        } else {
          toast.error("Project not found");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        toast.error("Failed to load project");
      }
    };

    fetchProject();
  }, [projectId]);

  const handleTechStackToggle = (tech) => {
    setSelectedTechStack(prev =>
      prev.includes(tech)
        ? prev.filter(t => t !== tech)
        : [...prev, tech]
    );
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 2 * 1024 * 1024) {
        toast.error('File size should be less than 2MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDeleteExistingFile = async () => {
    if (!existingFilePath) return;
    try {
      const fileRef = storageRef(storage, existingFilePath);
      await deleteObject(fileRef);
      setExistingFileUrl(null);
      setExistingFilePath(null);
      toast.success("Existing file deleted");
    } catch (err) {
      console.error("Error deleting file:", err);
      toast.error("Failed to delete file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedTechStack.length === 0) {
      toast.error("Please select at least one technology");
      return;
    }

    setLoading(true);

    try {
      const projectRef = ref(database, `projects/${projectId}`);
      const projectData = {
        uid: userProfile.uid,
        user_name: userProfile.full_name,
        title,
        description,
        github_link: githubLink,
        status,
        allow_collaboration: allowCollaboration,
        tech_stack: selectedTechStack,
        created_at: new Date().toISOString().split("T")[0],
        file_url: existingFileUrl,
        file_path: existingFilePath
      };

      // Upload new file if selected
      if (file) {
        if (existingFilePath) {
          const oldRef = storageRef(storage, existingFilePath);
          await deleteObject(oldRef);
        }
        const filePath = `project_files/${projectId}/${file.name}`;
        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);

        projectData.file_url = downloadURL;
        projectData.file_path = filePath;
      }

      await set(projectRef, {
        id: projectId,
        college_name: userProfile.college_name,
        ...projectData
      });

      toast.success("Project updated successfully");
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error("Firebase Error:", err);
      toast.error("Failed to update project");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Edit Project</h1>
          <p className="text-gray-600">Update your project details</p>
        </div>

        <Card className="glass border-white/50">
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
            <CardDescription>Edit the information about your project</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  placeholder="E-Commerce Platform"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              {/* Tech Stack */}
              <div>
                <Label>Tech Stack * (Select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-4 border rounded-lg bg-white/50">
                  {techStackOptions.map(tech => (
                    <Badge
                      key={tech}
                      variant={selectedTechStack.includes(tech) ? 'default' : 'outline'}
                      className={`cursor-pointer ${
                        selectedTechStack.includes(tech)
                          ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white'
                          : ''
                      }`}
                      onClick={() => handleTechStackToggle(tech)}
                    >
                      {tech} {selectedTechStack.includes(tech) && <X className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Project Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your project in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  required
                />
              </div>

              {/* GitHub Link */}
              <div>
                <Label htmlFor="github">GitHub Repository Link</Label>
                <Input
                  id="github"
                  type="url"
                  placeholder="https://github.com/username/repo"
                  value={githubLink}
                  onChange={(e) => setGithubLink(e.target.value)}
                />
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Project Status *</Label>
                <Select value={status} onValueChange={setStatus} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Idea Stage">Idea Stage</SelectItem>
                    <SelectItem value="In Development">In Development</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* File Upload */}
              <div>
                <Label htmlFor="file">Project File</Label>
                <p className="text-sm text-gray-500 mb-2">PDF, DOCX, or TXT (Max 2MB)</p>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileChange}
                />
                {file && (
                  <div className="mt-2 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-sky-500" />
                    <span className="text-sm text-gray-600">{file.name}</span>
                  </div>
                )}
                {!file && existingFileUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <a href={existingFileUrl} target="_blank" rel="noreferrer" className="text-sky-500 underline">
                      View existing file
                    </a>
                    <Button type="button" variant="destructive" onClick={handleDeleteExistingFile}>
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Collaboration */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="collaboration"
                  checked={allowCollaboration}
                  onCheckedChange={setAllowCollaboration}
                />
                <Label htmlFor="collaboration" className="cursor-pointer">
                  I'm open to collaboration on this project
                </Label>
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Project'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/feed')}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
